import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ArrowUpRight } from 'lucide-react';
import { FLAVORS, ASSETS } from '../lib/products';

const RANGE = [
  { key: 'masala', label: 'MASALA KRUNCH', sub: '50G · ₹50 · 15G PROTEIN · LIVE NOW', img: FLAVORS['masala-krunch'].img50, to: '/product/masala-krunch', color: '#E11D24' },
  { key: 'cheese', label: 'CHEESE & HERBS', sub: '50G · ₹50 · 15G PROTEIN · LIVE NOW', img: FLAVORS['cheese-herbs'].img50, to: '/product/cheese-herbs', color: '#F7B500' },
  { key: 'vote', label: 'FLAVOUR #3', sub: 'COMING SOON · STAY TUNED · VOTE NOW', img: ASSETS.soon, to: '/vote', color: '#E0632A' },
  { key: 'gifting', label: 'GIFT BOXES', sub: 'GIFT BOXES · CONTACT US ON WHATSAPP', img: ASSETS.box50, to: '/gifting', color: '#7A1E1E' },
];

const Visual = ({ item, active }) => {
  const h = active ? 'h-64 md:h-[420px]' : 'h-40 md:h-64';
  return (
    <div className={`relative ${h} flex items-center justify-center`}>
      {item.img2 && <img src={item.img2} alt="" loading="lazy" className={`absolute ${h} object-contain gpu -rotate-6 -translate-x-[30%] opacity-90`} />}
      <img src={item.img} alt={item.label} loading="lazy" className={`relative ${h} object-contain gpu ${item.img2 ? 'rotate-3 translate-x-[20%]' : ''}`} />
    </div>
  );
};

export default function RangeWheel() {
  const [i, setI] = useState(0);
  const navigate = useNavigate();
  const n = RANGE.length;
  const at = (k) => RANGE[((k % n) + n) % n];
  const active = at(i);
  const cur = ((i % n) + n) % n;

  return (
    <section id="range" data-testid="range-section" className="relative bg-cocoa text-cream overflow-hidden py-16 md:py-24">
      <div className="absolute -left-[38vw] md:-left-[22vw] top-1/2 -translate-y-1/2 w-[120vw] md:w-[70vw] aspect-square rounded-full transition-colors duration-500"
        style={{ background: active.color, boxShadow: '0 0 0 22px rgba(255,243,214,0.12), 0 0 0 44px rgba(255,243,214,0.06)' }} />

      <div className="relative max-w-[1500px] mx-auto px-5 md:px-14 grid md:grid-cols-[1.1fr_1fr] gap-10 items-center">
        <div className="relative h-[380px] md:h-[520px] flex items-center justify-center" data-cursor="expand" data-cursor-color="#FFF3D6">
          {[-1, 0, 1].map((off) => {
            const item = at(i + off);
            const isActive = off === 0;
            return (
              <motion.button key={`${item.key}-${off}`} data-testid={`range-item-${item.key}`} layout
                onClick={() => (isActive ? navigate(item.to) : setI(i + off))}
                initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: isActive ? 1 : 0.5, scale: isActive ? 1 : 0.7, x: off * (window.innerWidth < 768 ? 130 : 220), rotate: off * 12 }}
                whileHover={{ scale: isActive ? 1.05 : 0.78 }} transition={{ type: 'spring', stiffness: 200, damping: 22 }}
                className={`absolute ${isActive ? 'z-20' : 'z-10'}`}>
                <Visual item={item} active={isActive} />
              </motion.button>
            );
          })}
        </div>

        <div className="relative z-10">
          <p className="font-cond text-xs tracking-[0.4em] text-kyellow">OUR RANGE · {String(cur + 1).padStart(2, '0')} / {String(n).padStart(2, '0')}</p>
          <AnimatePresence mode="wait">
            <motion.div key={active.key} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.35 }}>
              <h2 data-testid="range-active-label" className="font-display text-6xl md:text-8xl leading-[0.85] mt-3">{active.label}</h2>
              <p className="font-cond text-sm md:text-base tracking-[0.25em] text-cream/70 mt-3">{active.sub}</p>
            </motion.div>
          </AnimatePresence>
          <div className="flex items-center gap-3 mt-8">
            <button data-testid="range-prev" data-cursor="plus" onClick={() => setI(i - 1)} aria-label="Previous"
              className="w-12 h-12 rounded-full border-2 border-cream/40 flex items-center justify-center hover:bg-cream hover:text-cocoa transition-colors"><ChevronLeft size={20} /></button>
            <button data-testid="range-next" data-cursor="plus" onClick={() => setI(i + 1)} aria-label="Next"
              className="w-12 h-12 rounded-full border-2 border-cream/40 flex items-center justify-center hover:bg-cream hover:text-cocoa transition-colors"><ChevronRight size={20} /></button>
            <button data-testid="range-go" data-cursor="plus" onClick={() => navigate(active.to)}
              className="ml-2 inline-flex items-center gap-2 rounded-full bg-kyellow text-cocoa font-cond font-bold text-sm tracking-wider px-5 h-12 border-2 border-kyellow hover:bg-cream transition-colors">
              OPEN <ArrowUpRight size={18} />
            </button>
          </div>
          <div className="flex gap-2 mt-8">
            {RANGE.map((r, k) => (
              <button key={r.key} data-testid={`range-thumb-${r.key}`} data-cursor="plus" onClick={() => setI(k)} aria-label={r.label}
                className={`h-2 rounded-full transition-all duration-300 ${k === cur ? 'w-10 bg-kyellow' : 'w-4 bg-cream/30 hover:bg-cream/60'}`} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
